import {
  PlayerPortalGetCompetitionDetailsResponse,
  PlayerPortalGetCompetitionHistoricMatchesResponse,
} from "@mixitone/actions";
import { isPresent, isValidEmailAddress, withStateToggle } from "@mixitone/util";
import { PortalController } from "controllers/PortalController";
import portalFunctions from "../../../lib/portalFunctions";

interface Props {
  params: { portalSlug: string; competitionId: string };
}

type TournamentSessionResponse = PlayerPortalGetCompetitionDetailsResponse["session"];
type TournamentCompetitionResponse = PlayerPortalGetCompetitionDetailsResponse["competition"];
type TournamentRegistrationResponse = PlayerPortalGetCompetitionDetailsResponse["registration"];
type TournamentPlayerResponse = PlayerPortalGetCompetitionDetailsResponse["player"];
type TournamentMatchResponse = PlayerPortalGetCompetitionDetailsResponse["match"];
type HistoricMatch = PlayerPortalGetCompetitionHistoricMatchesResponse["matches"][number];
type TournamentPlacementResponse = PlayerPortalGetCompetitionDetailsResponse["placement"];
type TournamentWinnersResponse = PlayerPortalGetCompetitionDetailsResponse["winners"];

interface State {
  loading: boolean;
  error: string | null;
  competition: TournamentCompetitionResponse | null;
  session: TournamentSessionResponse | null;
  registration: TournamentRegistrationResponse | null;
  player: TournamentPlayerResponse | null;
  match: TournamentMatchResponse | null;
  placement: TournamentPlacementResponse | null;
  winners: TournamentWinnersResponse;
  matchTimer: number | null; // seconds since match started
  historicMatches: HistoricMatch[];
  loadingHistoricMatches: boolean;
  partnerLink: string | null;
  partnerEmail: string;
  partnerInviteSent: boolean;
  sendingPartnerInvite: boolean;
}

export class CompetitionDetailPageController extends PortalController<State, Props> {
  private timerInterval: number | null = null;
  private pollingInterval: number | null = null;

  get initialState(): State {
    return {
      loading: true,
      error: null,
      competition: null,
      session: null,
      registration: null,
      player: null,
      match: null,
      placement: null,
      winners: [],
      matchTimer: null,
      historicMatches: [],
      loadingHistoricMatches: false,
      partnerLink: null,
      partnerEmail: "",
      partnerInviteSent: false,
      sendingPartnerInvite: false,
    };
  }

  async initialize(props: Props) {
    // Load competition details and historic matches in parallel
    await Promise.all([
      this.loadCompetitionDetails(props.params.competitionId),
      this.loadHistoricMatches(props.params.competitionId),
    ]);

    // Start polling every 5 seconds
    this.startPolling(props.params.competitionId);
  }

  override destroy() {
    if (this.timerInterval !== null) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    if (this.pollingInterval !== null) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private startPolling(competitionId: string) {
    this.pollingInterval = window.setInterval(() => {
      this.pollCompetitionDetails(competitionId);
    }, 5000); // Poll every 5 seconds
  }

  private applyCompetitionDetails(response: PlayerPortalGetCompetitionDetailsResponse) {
    this.state.competition = response.competition;
    this.state.session = response.session;
    this.state.registration = response.registration;
    this.state.player = response.player;
    this.state.match = response.match;
    this.state.placement = response.placement;
    this.state.winners = response.winners;
    this.updatePartnerInviteState(response);
  }

  private updatePartnerInviteState(response: PlayerPortalGetCompetitionDetailsResponse) {
    const { registration, session, competition } = response;
    const portalSlug = session?.portal_slug;

    if (registration.status === "waiting_partner" && registration.partner_code && portalSlug && competition) {
      this.state.partnerLink = `${window.location.origin}/tournament/${portalSlug}/register/${competition.id}?partnerCode=${registration.partner_code}`;
    } else {
      this.state.partnerLink = null;
    }

    // Don't overwrite in-progress email typing while an invite is being sent
    if (!this.state.sendingPartnerInvite) {
      this.state.partnerInviteSent = isPresent(registration.partner_email);
      if (this.state.partnerInviteSent || !this.state.partnerEmail) {
        this.state.partnerEmail = registration.partner_email || "";
      }
    }
  }

  private async pollCompetitionDetails(competitionId: string) {
    try {
      const response = await portalFunctions.getCompetitionDetails({ competitionId });

      // Update state without showing loading indicator
      const previousMatchStatus = this.state.match?.status;
      const previousMatchStartedAt = this.state.match?.started_at;

      this.applyCompetitionDetails(response);
      // Handle match timer updates
      if (response.match?.status === "in_progress" && response.match.started_at) {
        // If match just started or started_at changed, restart timer
        if (previousMatchStatus !== "in_progress" || previousMatchStartedAt !== response.match.started_at) {
          this.startMatchTimer(response.match.started_at);
        }
        // Otherwise, timer is already running and will continue
      } else {
        // Match is no longer in progress, stop timer
        if (this.timerInterval !== null) {
          clearInterval(this.timerInterval);
          this.timerInterval = null;
          this.state.matchTimer = null;
        }
      }
    } catch (error) {
      // Silently fail on polling errors - don't show error to user
      console.error("Error polling competition details:", error);
    }
  }

  @withStateToggle("loading")
  private async loadCompetitionDetails(competitionId: string) {
    try {
      const response = await portalFunctions.getCompetitionDetails({ competitionId });

      this.applyCompetitionDetails(response);

      // Start timer if match is in progress
      if (response.match?.status === "in_progress" && response.match.started_at) {
        this.startMatchTimer(response.match.started_at);
      }
    } catch (error) {
      console.error("Error loading competition details:", error);
      this.state.error = "Error loading competition details";
    }
  }

  @withStateToggle("loadingHistoricMatches")
  private async loadHistoricMatches(competitionId: string) {
    try {
      const response = await portalFunctions.getCompetitionHistoricMatches({ competitionId });
      this.state.historicMatches = response.matches;
    } catch (error) {
      console.error("Error loading historic matches:", error);
      // Don't set error state for historic matches - it's not critical
    }
  }

  private startMatchTimer(startedAt: string) {
    // Clear existing timer if any
    if (this.timerInterval !== null) {
      clearInterval(this.timerInterval);
    }

    const updateTimer = () => {
      const startTime = new Date(startedAt).getTime();
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 1000);
      this.state.matchTimer = elapsed;
    };

    updateTimer();
    this.timerInterval = window.setInterval(updateTimer, 1000);
  }

  actionClearError() {
    this.state.error = null;
  }

  async actionSendPartnerInvite() {
    const email = this.state.partnerEmail.trim();
    if (!this.state.registration?.id || !email) {
      this.state.error = "Partner email is required";
      return;
    }
    if (!isValidEmailAddress(email)) {
      this.state.error = "Enter a valid email address";
      return;
    }

    this.state.sendingPartnerInvite = true;

    try {
      const response = await portalFunctions.sendTournamentPartnerInvitation({
        tournamentRegistrationId: this.state.registration.id,
        email,
      });

      if (response.success) {
        this.state.partnerInviteSent = true;
        this.state.partnerEmail = email;
        if (this.state.registration) {
          this.state.registration.partner_email = email;
        }
      } else {
        this.state.error = "Failed to send partner invite. Please try again.";
      }
    } catch (error) {
      console.error("Error sending partner invite:", error);
      this.state.error = "Error sending partner invite. Please try again or contact support@mixitone.com";
    } finally {
      this.state.sendingPartnerInvite = false;
    }
  }

  async actionCheckIn() {
    if (!this.state.competition) return;

    try {
      const response = await portalFunctions.checkInPlayer({
        competitionId: this.state.competition.id,
      });

      // Update player state
      if (this.state.player) {
        this.state.player.checked_in_at = response.checked_in_at;
      }
    } catch (error) {
      console.error("Error checking in:", error);
      this.state.error = "Error checking in";
    }
  }

  async actionStartMatch() {
    if (!this.state.match) return;

    try {
      const response = await portalFunctions.startMatch({
        matchId: this.state.match.id,
      });

      // Update match state
      this.state.match.status = response.status;
      this.state.match.started_at = response.started_at;

      // Start timer
      if (response.started_at) {
        this.startMatchTimer(response.started_at);
      }
    } catch (error) {
      console.error("Error starting match:", error);
      this.state.error = "Error starting match";
    }
  }

  async actionFinishMatch() {
    if (!this.state.match) return;

    try {
      const response = await portalFunctions.finishMatch({
        matchId: this.state.match.id,
      });

      // Update match state
      this.state.match.status = response.status;

      // Stop timer
      if (this.timerInterval !== null) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
        this.state.matchTimer = null;
      }

      // Reload competition details to get updated state
      if (this.state.competition) {
        await this.loadCompetitionDetails(this.state.competition.id);
        await this.loadHistoricMatches(this.state.competition.id);
      }
    } catch (error) {
      console.error("Error finishing match:", error);
      this.state.error = "Error finishing match";
    }
  }

  formatTimer(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  }
}
